/**
 * @module Dashboard
 * @description Operations dashboard — live overview of document queues,
 * lifecycle metrics, and recently filed documents with server-side sorting,
 * filtering, and pagination.
 *
 * @dependencies
 * - GET /dashboard           → aggregate queue counts (StatCards)
 * - GET /dashboard/recent    → paginated + filterable recent-documents table
 * - POST /documents/:id/interaction → click telemetry (non-admin only)
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../components/layout/PageHeader';
import { StatCard } from '../components/ui/StatCard';
import { Pagination } from '../components/ui/Pagination';
import { Pill } from '../components/ui/Pill';
import { http } from '../lib/fetcher';
import { useMe } from '../auth/useMe';
import type {
  DashboardResponse,
  Paged,
  PrimaryObject,
  RecentDocument,
  RecentSortBy,
  RecentSortDir,
} from '../api/types';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Page size for the "recently filed" table. */
const TAKE = 25;

/** Prevents column collapse on narrow viewports; triggers horizontal scroll. */
const RECENT_TABLE_MIN_WIDTH = 1120;

/** Maps primary-object types to the brand accent cycle for row stripes. */
const PRIMARY_OBJECT_ACCENT: Record<string, string> = {
  Deal: 'petrol',
  Entity: 'teal',
  Supplier: 'orange',
  Investor: 'green',
  Function: 'slate',
};

/** Realistic per-column skeleton widths for a more polished shimmer. */
const SKELETON_WIDTHS = [72, 48, 40, 55, 65, 58, 58, 30, 36];

// ─── Types ───────────────────────────────────────────────────────────────────

/** Empty string = "All" (no filter applied). */
type SourceFilter = '' | 'Email' | 'OneDrive' | 'Manual';
type PrimaryObjectFilter = '' | PrimaryObject;

/** Query parameters sent to GET /dashboard/recent. */
interface RecentParams {
  skip: number;
  take: number;
  q: string;
  from: string;
  to: string;
  sourceType: SourceFilter;
  primaryObject: PrimaryObjectFilter;
  sortBy: RecentSortBy;
  sortDir: RecentSortDir;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function DashboardPage() {
  const me = useMe();
  const queryClient = useQueryClient();

  // ── Data fetching ────────────────────────────────────────────────────────

  const dashboard = useQuery<DashboardResponse>({
    queryKey: ['dashboard'],
    queryFn: () => http.get<DashboardResponse>('/dashboard'),
  });

  // ── Filter & pagination state ────────────────────────────────────────────

  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sourceType, setSourceType] = useState<SourceFilter>('');
  const [primaryObject, setPrimaryObject] = useState<PrimaryObjectFilter>('');
  const [sortBy, setSortBy] = useState<RecentSortBy>('filedAt');
  const [sortDir, setSortDir] = useState<RecentSortDir>('desc');

  const params: RecentParams = { skip, take: TAKE, q, from, to, sourceType, primaryObject, sortBy, sortDir };
  const hasFilters = q !== '' || from !== '' || to !== '' || sourceType !== '' || primaryObject !== '';

  // Count of active filters for the badge
  const filterCount = [q, from, to, sourceType, primaryObject].filter(Boolean).length;

  // Negate JS's getTimezoneOffset() (west-positive) → minutes east of UTC.
  // Sent alongside naive datetime-local values so the backend can anchor
  // `from`/`to` to the operator's wall clock instead of UTC.
  const tzOffset = -new Date().getTimezoneOffset();

  const recent = useQuery<Paged<RecentDocument>>({
    queryKey: ['dashboard-recent', params, tzOffset],
    queryFn: () =>
      http.get<Paged<RecentDocument>>('/dashboard/recent', {
        skip,
        take: TAKE,
        q: q || undefined,
        from: from || undefined,
        to: to || undefined,
        tzOffset,
        sourceType: sourceType || undefined,
        primaryObject: primaryObject || undefined,
        sortBy,
        sortDir,
      }),
    placeholderData: (prev) => prev,
  });

  // ── Interaction telemetry ────────────────────────────────────────────────

  const isAdmin = me.data?.isAdmin ?? false;

  // Fire-and-forget click telemetry. The backend rejects admin callers too,
  // but skipping client-side avoids the round-trip when we already know.
  const recordInteraction = useMutation({
    mutationFn: (documentId: number) =>
      http.post<void>(`/documents/${documentId}/interaction`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-recent'] });
    },
  });

  const onInteract = (doc: RecentDocument) => {
    if (!isAdmin) {
      recordInteraction.mutate(doc.documentId);
    }
  };

  // ── Derived values ───────────────────────────────────────────────────────

  const counts = dashboard.data?.counts;
  const countsLoading = dashboard.isPending;
  const total = recent.data?.total ?? 0;
  const rows = recent.data?.items ?? [];

  // ── Sort & filter handlers ───────────────────────────────────────────────

  const resetPage = () => setSkip(0);

  /** Toggle direction if same column; otherwise default asc for text, desc for dates/counts. */
  const toggleSort = (column: RecentSortBy) => {
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir(column === 'filename' || column === 'location' || column === 'sourceOwner' ? 'asc' : 'desc');
    }
    resetPage();
  };

  const clearFilters = () => {
    setQ('');
    setFrom('');
    setTo('');
    setSourceType('');
    setPrimaryObject('');
    resetPage();
  };

  // ── Column definitions ───────────────────────────────────────────────────

  const sortColumns = useMemo<Array<{ key: RecentSortBy; label: string; width: string }>>(
    () => [
      { key: 'filename', label: 'Filename', width: '18%' },
      { key: 'primaryObject', label: 'Primary object', width: '9%' },
      { key: 'sourceType', label: 'Source', width: '7%' },
      { key: 'sourceOwner', label: 'Source Owner', width: '10%' },
      { key: 'location', label: 'Location', width: '13%' },
      { key: 'sourceObservedAt', label: `Arrived at (${formatTzOffset()})`, width: '11%' },
      { key: 'filedAt', label: `Filed at (${formatTzOffset()})`, width: '11%' },
      { key: 'interactionCount', label: 'Interactions', width: '7%' },
    ],
    []
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <PageHeader
        title="Operations dashboard"
        subtitle="Live overview of queues, lifecycle moves, and today's filings"
        actions={
          dashboard.data?.sharePointRootUrl && (
            <a
              href={`${dashboard.data.sharePointRootUrl.replace(/\/$/, '')}/DIH/Forms/AllItems.aspx`}
              target="_blank"
              rel="noreferrer"
              className="brand-btn brand-btn-primary"
            >
              <SharePointIcon />
              Open in SharePoint
            </a>
          )
        }
      />

      <section className="page-pad space-y-6">

        {/* ── Error banner (dashboard metrics) ──────────────────────────── */}
        {dashboard.error && (
          <div className="brand-note-error flex items-start gap-2" role="alert">
            <InfoCircleIcon size={15} />
            <span>Failed to load dashboard metrics: {(dashboard.error as Error).message}</span>
          </div>
        )}

        {/* ── Queue status stat cards ───────────────────────────────────── */}
        <div>
          <div className="brand-section-label mb-3 animate-fade-in">Queue status</div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            <div className="animate-fade-in-up" style={{ animationDelay: '0ms' }}>
              <StatCard
                accent={1}
                label="Total Scanned"
                value={countsLoading ? <Skeleton /> : counts?.totalDocs ?? 0}
                caption="All documents tracked in the system"
                icon={<ScanIcon />}
              />
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '60ms' }}>
              <StatCard
                accent={2}
                label="Scanned Today"
                value={countsLoading ? <Skeleton /> : counts?.documentsFiledToday ?? 0}
                caption="Documents scanned today"
                icon={<CalendarIcon />}
              />
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '120ms' }}>
              <StatCard
                accent={3}
                label="Open Triage"
                value={countsLoading ? <Skeleton /> : counts?.openTriage ?? 0}
                caption="Items needing human routing"
                href="/triage"
                icon={<InboxIcon />}
              />
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '180ms' }}>
              <StatCard
                accent={4}
                label="Filed Docs"
                value={countsLoading ? <Skeleton /> : counts?.filedDocs ?? 0}
                caption="Documents currently on SharePoint"
                icon={<SparklesIcon />}
              />
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '240ms' }}>
              <StatCard
                accent={1}
                label="Skipped Docs"
                value={countsLoading ? <Skeleton /> : counts?.skippedDocs ?? 0}
                caption={isAdmin ? 'Screener drops + manual dismissals' : 'Screener drops + manual dismissals (admin only)'}
                href={isAdmin ? '/admin/skipped' : undefined}
                icon={<SkipIcon />}
              />
            </div>
          </div>
        </div>

        {/* ── Recently filed: filters + sortable table + pagination ──── */}
        <div className="animate-fade-in-up" style={{ animationDelay: '360ms' }}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <div className="brand-section-label">Recently filed</div>
              {filterCount > 0 && (
                <span className="brand-pill brand-pill-petrol" style={{ fontSize: '0.625rem', padding: '0.125rem 0.375rem' }}>
                  {filterCount} {filterCount === 1 ? 'filter' : 'filters'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Record count chip */}
              <span
                className="inline-flex items-center gap-1.5"
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'var(--color-petrol)',
                  backgroundColor: 'rgba(0, 86, 119, 0.06)',
                  padding: '0.25rem 0.625rem',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                {recent.isPending ? (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--color-petrol)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin" aria-hidden="true">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Loading
                  </>
                ) : (
                  <>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                    </svg>
                    {total.toLocaleString()} {total === 1 ? 'record' : 'records'}
                  </>
                )}
              </span>

              {/* Last refreshed chip */}
              {recent.dataUpdatedAt > 0 && (
                <span
                  className="inline-flex items-center gap-1.5"
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--color-faint-slate)',
                    backgroundColor: 'rgba(100, 116, 139, 0.06)',
                    padding: '0.25rem 0.625rem',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                  {formatLocal(new Date(recent.dataUpdatedAt).toISOString())}
                </span>
              )}

              {hasFilters && (
                <button type="button" className="brand-link-btn text-[0.75rem]" onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Filter bar */}
          <div className="brand-card mb-3" style={{ padding: '0.875rem 1rem' }}>
            <div className="grid gap-2.5 md:grid-cols-2 lg:grid-cols-6">
              <div className="lg:col-span-2">
                <label htmlFor="dash-search" className="brand-label">Search</label>
                <div className="search-input-wrap">
                  <svg className="search-input-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                  </svg>
                  <input
                    id="dash-search"
                    className="brand-input"
                    placeholder="Filename or location…"
                    value={q}
                    onChange={(e) => { setQ(e.target.value); resetPage(); }}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="dash-from" className="brand-label">From</label>
                <input
                  id="dash-from"
                  type="datetime-local"
                  className="brand-input"
                  value={from}
                  onChange={(e) => { setFrom(e.target.value); resetPage(); }}
                />
              </div>
              <div>
                <label htmlFor="dash-to" className="brand-label">To</label>
                <input
                  id="dash-to"
                  type="datetime-local"
                  className="brand-input"
                  value={to}
                  onChange={(e) => { setTo(e.target.value); resetPage(); }}
                />
              </div>
              <div>
                <label htmlFor="dash-source" className="brand-label">Source</label>
                <select
                  id="dash-source"
                  className="brand-input"
                  value={sourceType}
                  onChange={(e) => { setSourceType(e.target.value as SourceFilter); resetPage(); }}
                >
                  <option value="">All</option>
                  <option value="Email">Email</option>
                  <option value="OneDrive">OneDrive</option>
                  <option value="Manual">Manual</option>
                </select>
              </div>
              <div>
                <label htmlFor="dash-primary" className="brand-label">Primary object</label>
                <select
                  id="dash-primary"
                  className="brand-input"
                  value={primaryObject}
                  onChange={(e) => { setPrimaryObject(e.target.value as PrimaryObjectFilter); resetPage(); }}
                >
                  <option value="">All</option>
                  <option value="Deal">Deal</option>
                  <option value="Entity">Entity</option>
                  <option value="Supplier">Supplier</option>
                  <option value="Investor">Investor</option>
                  <option value="Function">Function</option>
                </select>
              </div>
            </div>
          </div>

          {/* Error banner (recent documents) */}
          {recent.error && (
            <div className="brand-note-error flex items-start gap-2 mb-3" role="alert">
              <InfoCircleIcon size={15} />
              <span>Failed to load recent documents: {(recent.error as Error).message}</span>
            </div>
          )}

          {/* Data table with sticky header */}
          <div className="brand-card" style={{ padding: 0 }}>
            <div className="brand-card-bar" style={{ backgroundColor: 'var(--color-petrol)' }} />
            <div className="brand-table-sticky-head">
              <table className="brand-table" style={{ minWidth: RECENT_TABLE_MIN_WIDTH }} aria-label="Recently filed documents">
                <colgroup>
                  {sortColumns.map((c) => (
                    <col key={c.key} style={{ width: c.width }} />
                  ))}
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead>
                  <tr>
                    {sortColumns.map((c) => (
                      <SortableHeader
                        key={c.key}
                        label={c.label}
                        active={sortBy === c.key}
                        dir={sortDir}
                        onClick={() => toggleSort(c.key)}
                      />
                    ))}
                    <th>SharePoint</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Skeleton rows — per-column widths for realistic shimmer */}
                  {recent.isPending && Array.from({ length: 6 }).map((_, i) => (
                    <tr key={`skel-${i}`}>
                      {SKELETON_WIDTHS.map((w, j) => (
                        <td key={j}>
                          <div
                            className="animate-pulse"
                            style={{
                              height: 12,
                              backgroundColor: 'var(--color-divider)',
                              borderRadius: 'var(--radius-sm)',
                              width: `${w + (i * 7) % 15}%`,
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}

                  {/* Empty state with contextual CTA */}
                  {!recent.isPending && rows.length === 0 && (
                    <tr>
                      <td colSpan={9}>
                        <div className="flex flex-col items-center gap-3 py-8 text-center">
                          <div style={{ padding: '1rem', borderRadius: '50%', backgroundColor: 'rgba(0, 86, 119, 0.04)' }}>
                            <FileCheckIcon />
                          </div>
                          <div>
                            <div className="text-[0.875rem] font-semibold" style={{ color: 'var(--color-navy)' }}>
                              {hasFilters ? 'No documents match' : 'Nothing filed yet'}
                            </div>
                            <div className="text-[0.8125rem] text-muted-slate mt-0.5">
                              {hasFilters
                                ? 'Try adjusting your filters or clearing them to see all documents.'
                                : 'Documents will appear here once the scanner processes incoming mail and OneDrive files.'}
                            </div>
                          </div>
                          {hasFilters ? (
                            <button type="button" className="brand-btn brand-btn-secondary brand-btn-sm" onClick={clearFilters}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                              Clear all filters
                            </button>
                          ) : (counts?.openTriage ?? 0) > 0 ? (
                            <Link to="/triage" className="brand-btn brand-btn-primary brand-btn-sm" style={{ textDecoration: 'none' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
                              </svg>
                              Go to Triage
                            </Link>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Data rows — accent stripe by primary-object type */}
                  {!recent.isPending && rows.map((doc) => (
                    <tr
                      key={doc.documentId}
                      data-accent={doc.primaryObject ? PRIMARY_OBJECT_ACCENT[doc.primaryObject] ?? 'petrol' : undefined}
                      onClick={() => onInteract(doc)}
                      style={{ cursor: 'pointer' }}
                      tabIndex={0}
                      role="button"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onInteract(doc);
                        }
                      }}
                    >
                      <td className="cell-truncate font-semibold" title={doc.filename}>{doc.filename}</td>
                      <td className="cell-truncate">
                        {doc.primaryObject
                          ? <Pill tone={primaryObjectTone(doc.primaryObject)}>{doc.primaryObject}</Pill>
                          : '—'}
                      </td>
                      <td className="cell-truncate">
                        {doc.sourceType ?? <span className="text-muted-slate">—</span>}
                      </td>
                      <td className="cell-truncate" title={doc.sourceOwner ?? undefined}>
                        {doc.sourceOwner ?? <span className="text-muted-slate">—</span>}
                      </td>
                      <td className="cell-truncate" style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }} title={doc.location ?? undefined}>
                        {doc.location ?? <span className="text-muted-slate">—</span>}
                      </td>
                      <td
                        style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}
                        title={
                          doc.sourceObservedAt
                            ? doc.sourceType === 'Email'
                              ? 'Email received in mailbox at this time (M365 receivedDateTime).'
                              : doc.sourceType === 'OneDrive'
                                ? 'File uploaded to OneDrive at this time (driveItem createdDateTime).'
                                : 'Source-side timestamp recorded when the document was first observed.'
                            : undefined
                        }
                      >
                        {doc.sourceObservedAt ? formatLocal(doc.sourceObservedAt) : <span className="text-muted-slate">—</span>}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>
                        {formatLocal(doc.filedAt)}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', textAlign: 'right' }}>
                        {doc.interactionCount}
                      </td>
                      <td>
                        {doc.canonicalSharePointUrl ? (
                          <a
                            href={doc.canonicalSharePointUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="brand-link-btn text-[0.8125rem]"
                            onClick={(e) => {
                              e.stopPropagation();
                              onInteract(doc);
                            }}
                          >
                            Open
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline-block ml-1" aria-hidden="true">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </a>
                        ) : (
                          <span className="text-muted-slate">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination skip={skip} take={TAKE} total={total} onPage={setSkip} />
        </div>

        {/* ── Operator tip callout ──────────────────────────────────────── */}
        <div className="brand-note flex items-start gap-2.5 animate-fade-in" style={{ animationDelay: '420ms' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent-3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5" aria-hidden="true">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            <span className="font-semibold" style={{ color: 'var(--color-body)', fontStyle: 'normal' }}>Operator tip</span>
            <span className="mx-1.5" style={{ color: 'var(--color-divider)' }}>|</span>
            Triage drives auto-file confidence over time — resolve open items early in the morning, then process backfill in batches.
          </div>
        </div>
      </section>
    </>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

/** Sortable column header with directional arrow indicator. */
interface SortableHeaderProps {
  label: string;
  active: boolean;
  dir: RecentSortDir;
  onClick: () => void;
}

function SortableHeader({ label, active, dir, onClick }: SortableHeaderProps) {
  return (
    <th
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={label}
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={`Sort by ${label}${active ? (dir === 'asc' ? ', ascending' : ', descending') : ''}`}
        style={{
          alignItems: 'center',
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          padding: 0,
          cursor: 'pointer',
          display: 'inline-flex',
          gap: '0.25rem',
          font: 'inherit',
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          verticalAlign: 'bottom',
          whiteSpace: 'nowrap',
        }}
      >
        <span
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
        <span style={{ display: 'inline-flex', flex: '0 0 auto' }}>
          <SortIcon active={active} dir={dir} />
        </span>
      </button>
    </th>
  );
}

/** Bi-directional sort arrow; inactive shows both chevrons, active shows one. */
function SortIcon({ active, dir }: { active: boolean; dir: RecentSortDir }) {
  const color = active ? 'currentColor' : 'var(--color-faint-slate)';
  if (!active) {
    return (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M7 15l5 5 5-5" /><path d="M7 9l5-5 5 5" />
      </svg>
    );
  }
  return dir === 'asc' ? (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 14l5-5 5 5" />
    </svg>
  ) : (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 10l5 5 5-5" />
    </svg>
  );
}

// ─── Utilities ─────────────────────────────────────────────────────────────────

/** Maps primary-object type to Pill tone for the table cells. */
function primaryObjectTone(po: string): 'petrol' | 'teal' | 'orange' | 'green' | 'slate' {
  switch (po) {
    case 'Deal': return 'petrol';
    case 'Entity': return 'teal';
    case 'Supplier': return 'orange';
    case 'Investor': return 'green';
    default: return 'slate';
  }
}

/** Formats the browser's timezone offset as "UTC+05:00". */
function formatTzOffset(): string {
  const offsetMin = -new Date().getTimezoneOffset();
  const sign = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${hh}:${mm}`;
}

/** Converts an ISO timestamp to local "YYYY-MM-DD HH:mm" display string. */
function formatLocal(iso: string): string {
  // .NET may serialize DateTimeKind.Unspecified without trailing "Z";
  // treat offset-less timestamps as UTC so the local conversion is correct.
  const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ─── Icons ─────────────────────────────────────────────────────────────────────

/** Placeholder pulse block shown while stat card values load. */
function Skeleton() {
  return (
    <div className="animate-pulse" style={{ width: 48, height: 28, backgroundColor: 'var(--color-divider)', borderRadius: 'var(--radius-sm)' }} />
  );
}

/** Reusable info-circle icon for error banners. */
function InfoCircleIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 mt-0.5">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function ScanIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" /><line x1="7" y1="12" x2="17" y2="12" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

function SparklesIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z" />
    </svg>
  );
}

function SkipIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );
}

/** Grid/table icon used for the "Open in SharePoint" action button. */
function SharePointIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <path d="M2 10h20" />
      <path d="M12 4v16" />
    </svg>
  );
}

/** Document-with-checkmark icon for the table empty state. */
function FileCheckIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: 'var(--color-petrol)' }}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M9 15l2 2 4-4" />
    </svg>
  );
}
